import { useCallback, useEffect, useRef } from "react";

import { useSegmentContext } from "../context/segment.context";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EventProperties = Record<string, any>;

export type Track = (eventName: string, properties?: EventProperties) => void;

/**
 * AS-1832 — return a `track(name, props?)` function that merges the
 * Segment context defaults (sessionId, interviewLocationId, UTM) into
 * every event. When no write key is configured the call is a silent
 * no-op so local dev / tests don't need to mock the wire.
 */
export const useTrackAnalytics = (
  additionalDefaults: EventProperties = {},
): Track => {
  const { analytics, defaults } = useSegmentContext();

  // Keep the latest `additionalDefaults` in a ref so callers can pass
  // inline object literals without rebuilding `track` every render, while
  // still reading the current value (avoids stale-closure bugs when props
  // like `interviewLocationId` change after mount).
  const additionalDefaultsRef = useRef(additionalDefaults);
  additionalDefaultsRef.current = additionalDefaults;

  return useCallback(
    (eventName: string, properties: EventProperties = {}) => {
      if (!analytics) {
        return;
      }
      analytics.track(eventName, {
        ...defaults,
        ...additionalDefaultsRef.current,
        ...properties,
      });
    },
    [analytics, defaults],
  );
};

/**
 * Fire a `page` event for the current page on mount. Mirrors v2's
 * `usePageAnalyticsEffect` so dashboards land on consistent event names.
 */
export const usePageViewAnalytics = (name: string) => {
  const { analytics, defaults } = useSegmentContext();

  useEffect(() => {
    if (!analytics) {
      return;
    }
    analytics.page(name, { ...defaults });
    // Fire once per mount only — re-firing on every state change would
    // double-count page views.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);
};
